import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { CheckIcon } from '@heroicons/react/outline';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import PublicOnlyRoute from 'components/Navigation/PublicOnlyRoute';
import ExternalHeader from 'components/Navigation/ExternalHeader';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';

import SignupStepPersonal from './SignupSteps/StepPersonal';
import SignupStepCompany from './SignupSteps/StepCompany';
import SignupStepLegal from './SignupSteps/StepLegal';
import SignupStep1 from './SignupSteps/Step1';
import SignupStep3 from './SignupSteps/Step3';

import { classNames } from 'utils/functions';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

export default function SignupPage() {
  const {
    register,
    watch,
    control,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      document_type: 'DNI',
      pep: 'No',
      country: 'PE',
      department: 'LIMA',
      province: 'LIMA',
      district: 'LIMA',
      legal_document_type: 'DNI',
      legal_pep: 'No',
      legal_country: 'PE',
      legal_department: 'LIMA',
      legal_province: 'LIMA',
      legal_district: 'LIMA',
    },
  });
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [companyFile, setCompanyFile] = useState<File | null>(null);
  const [legalFile, setLegalFile] = useState<File | null>(null);

  const documentType = watch('document_type');
  const acceptTerms = watch('terms');
  const acceptPrivacy = watch('policy');

  const submitSignup = async (data: any) => {
    setLoading(true);

    let finalData = new FormData();
    finalData.append('document_type', data.document_type);
    finalData.append('number', data.number);
    finalData.append('password', data.password);
    finalData.append('accept_offers', data.offers);
    if (data.document_type === 'RUC') {
      if (companyFile) {
        finalData.append('ruc_file', companyFile);
      }
      if (legalFile) {
        finalData.append('validity', legalFile);
      }
      finalData.append('department', data.department);
      finalData.append('province', data.province);
      finalData.append('district', data.district);
      finalData.append('commercial_name', data.commercial_name);
      finalData.append('economic_activity', data.economic_activity);
      finalData.append('commercial_address', data.commercial_address);
      finalData.append('cellphone', data.cellphone);
      if (data.telephone !== '') finalData.append('telephone', data.telephone);
      finalData.append('email', data.email);
      const legalData: any = {
        document_type: data.legal_document_type,
        number: data.legal_number,
        name: data.legal_name,
        lname_p: data.legal_lname_p,
        lname_m: data.legal_lname_m,
        country: data.legal_country,
        address: data.legal_address,
        cellphone: data.legal_cellphone,
        email: data.legal_email,
        job: data.legal_job,
        pep: data.legal_pep === 'Si',
        pep_institute: data.legal_pep_institute,
        pep_since: data.legal_pep_since,
      };
      if (data.legal_telephone !== '') legalData.telephone = data.legal_telephone;
      finalData.append('rl', JSON.stringify(legalData));
    } else {
      if (documentFile) {
        finalData.append('doc_file', documentFile);
      }
      finalData.append('name', data.name);
      finalData.append('lname_p', data.lname_p);
      finalData.append('lname_m', data.lname_m);
      finalData.append('country', data.country);
      finalData.append('address', data.address);
      if (data.country === 'PE') {
        finalData.append('department', data.department);
        finalData.append('province', data.province);
        finalData.append('district', data.district);
      } else {
        finalData.append('department', data.department || '-');
        finalData.append('province', data.province || '-');
        finalData.append('district', data.district || '-');
      }
      finalData.append('cellphone', data.cellphone);
      finalData.append('email', data.email);
      finalData.append('job', data.job);
      if (data.telephone !== '') finalData.append('telephone', data.telephone);
      finalData.append('pep', data.pep === 'Si' ? 'true' : 'false');
      if (data.pep === 'Si') {
        finalData.append('pep_institute', data.pep_institute);
        finalData.append('pep_since', data.pep_since);
      }
    }

    let result: any = await generalApi.post(
      data.document_type === 'RUC' ? '/pj_register' : '/pn_register',
      finalData
    );
    if (!result.success) {
      setLoading(false);
      return showErrorToast(result.data?.err || 'Ocurrió un error');
    }
    showSuccessToast(
      'Registro completo, revise su correo electronico, para la activación de su cuenta'
    );
    setTimeout(function () {
      history('/login');
    }, 3000);
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        placeholder={`Ingrese su ${label}`}
        type={type}
        name={name}
        register={register}
        watch={watch}
        control={control}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };

  const steps = [
    {
      id: '01',
      name: 'Identificación',
      width: 'max-w-4xl',
      content: (
        <SignupStep1
          buildBaseInput={buildBaseInput}
          setValue={setValue}
          watch={watch}
          generalApi={generalApi}
          documentFile={documentFile}
          setDocumentFile={setDocumentFile}
        />
      ),
      onContinue: async () => {
        if (getValues('document_type') !== 'RUC' && !documentFile)
          return showErrorToast('Ingrese un archivo válido');
        if (await trigger(['document_type', 'number'])) setCurrentStep(1);
      },
    },
    {
      id: '02',
      name: documentType === 'RUC' ? 'Datos de Empresa' : 'Datos personales',
      width: 'max-w-7xl',
      content:
        documentType === 'RUC' ? (
          <SignupStepCompany
            buildBaseInput={buildBaseInput}
            setValue={setValue}
            watch={watch}
            generalApi={generalApi}
            documentFile={companyFile}
            setDocumentFile={setCompanyFile}
          />
        ) : (
          <SignupStepPersonal
            buildBaseInput={buildBaseInput}
            setValue={setValue}
            watch={watch}
            generalApi={generalApi}
          />
        ),
      onContinue: async () => {
        if (getValues('document_type') === 'RUC' && !companyFile)
          return showErrorToast('Ingrese un archivo válido');
        if (
          await trigger(
            documentType === 'RUC'
              ? [
                  'commercial_name',
                  'economic_activity',
                  'commercial_address',
                  'email',
                  'cellphone',
                  'department',
                  'province',
                  'district',
                ]
              : [
                  'name',
                  'lname_p',
                  'lname_m',
                  'address',
                  'job',
                  'email',
                  'cellphone',
                  'pep',
                  'pep_institute',
                  'pep_since',
                  'country',
                  'department',
                  'province',
                  'district',
                ]
          )
        )
          setCurrentStep(2);
      },
    },
    ...(documentType === 'RUC'
      ? [
          {
            id: '03',
            name: 'Representante Legal',
            width: 'max-w-7xl',
            content: (
              <SignupStepLegal
                buildBaseInput={buildBaseInput}
                setValue={setValue}
                watch={watch}
                generalApi={generalApi}
                documentFile={legalFile}
                setDocumentFile={setLegalFile}
              />
            ),
            onContinue: async () => {
              if (
                await trigger([
                  'legal_number',
                  'legal_name',
                  'legal_lname_p',
                  'legal_lname_m',
                  'legal_address',
                  'legal_job',
                  'legal_email',
                  'legal_cellphone',
                  'legal_pep',
                  'legal_pep_institute',
                  'legal_pep_since',
                  'legal_country',
                ])
              )
                setCurrentStep(3);
            },
          },
        ]
      : []),
    {
      id: documentType === 'RUC' ? '04' : '03',
      name: 'Contraseña',
      width: 'max-w-4xl',
      content: (
        <SignupStep3 buildBaseInput={buildBaseInput} setValue={setValue} register={register} />
      ),
    },
  ];

  return (
    <PublicOnlyRoute>
      <div className="min-h-full bg-gray-100">
        <ExternalHeader />

        <div
          className={classNames(
            steps[currentStep].width,
            'mx-auto mt-8 rounded-lg bg-white shadow-lg transition-all duration-500'
          )}
        >
          <nav aria-label="Progress">
            <ol className="divide-y divide-gray-300 border-b border-gray-300 md:flex md:divide-y-0">
              {steps.map((step, stepIdx) => (
                <li key={step.name} className="relative md:flex md:flex-1">
                  {stepIdx < currentStep ? (
                    <div className="group flex w-full cursor-pointer items-center">
                      <span className="flex items-center px-6 py-4 text-sm font-medium">
                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 transition-all duration-500 group-hover:bg-primary-800">
                          <CheckIcon className="h-6 w-6 text-white" aria-hidden="true" />
                        </span>
                        <span className="ml-4 text-sm font-medium text-gray-900">{step.name}</span>
                      </span>
                    </div>
                  ) : stepIdx === currentStep ? (
                    <div
                      className="flex cursor-pointer items-center px-6 py-4 text-sm font-medium"
                      aria-current="step"
                    >
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 border-primary-600">
                        <span className="text-primary-600">{step.id}</span>
                      </span>
                      <span className="ml-4 text-sm font-medium text-primary-600">{step.name}</span>
                    </div>
                  ) : (
                    <div className="group flex cursor-pointer items-center">
                      <span className="flex items-center px-6 py-4 text-sm font-medium">
                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 border-gray-300 transition-all duration-500 group-hover:border-gray-400">
                          <span className="text-gray-500 transition-all duration-500 group-hover:text-gray-900">
                            {step.id}
                          </span>
                        </span>
                        <span className="ml-4 text-sm font-medium text-gray-500 transition-all duration-500 group-hover:text-gray-900">
                          {step.name}
                        </span>
                      </span>
                    </div>
                  )}

                  {stepIdx !== steps.length - 1 ? (
                    <>
                      {/* Arrow separator for lg screens and up */}
                      <div
                        className="absolute top-0 right-0 hidden h-full w-5 md:block"
                        aria-hidden="true"
                      >
                        <svg
                          className="h-full w-full text-gray-300"
                          viewBox="0 0 22 80"
                          fill="none"
                          preserveAspectRatio="none"
                        >
                          <path
                            d="M0 -2L20 40L0 82"
                            vectorEffect="non-scaling-stroke"
                            stroke="currentcolor"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </>
                  ) : null}
                </li>
              ))}
            </ol>
          </nav>
          <form onSubmit={handleSubmit(submitSignup)}>
            <div className="px-8 py-12">{steps[currentStep].content}</div>
          </form>

          <div className="flex justify-center space-x-8 rounded-b-lg bg-gray-50 py-6">
            <SolidButton
              className="w-auto px-8"
              isDisabled={currentStep === 0 || loading}
              onClick={() => setCurrentStep(currentStep - 1)}
            >
              Regresar
            </SolidButton>
            <SolidButton
              className="w-auto px-8"
              isLoading={loading}
              isDisabled={currentStep === steps.length - 1 && (!acceptTerms || !acceptPrivacy)}
              onClick={
                currentStep === steps.length - 1
                  ? handleSubmit(submitSignup)
                  : () => steps[currentStep].onContinue?.()
              }
            >
              {currentStep === steps.length - 1 ? 'Registrarme' : 'Continuar'}
            </SolidButton>
          </div>
        </div>
      </div>
    </PublicOnlyRoute>
  );
}
